import { useAppSelector } from 'app/hooks';
import { AxiosError } from 'axios';
import AlertModal from 'components/common/AlertModal';
import ConfirmModal from 'components/common/ConfirmModal';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import SelectTargetMenuModal from 'components/SelectTargetMenuModal';
import { selectIsEditMode } from 'features/common/editModeSlice';
import { CommonResponse, TargetMenu } from 'models';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  displayError,
  handleServiceError,
  TARGET_MENU_BAR_GUIDANCE,
  TARGET_MENU_DAILY_MENU,
  TARGET_MENU_DEFAULT,
  TARGET_MENU_ORDER_QUANTITY_LIMIT,
  TARGET_MENU_OVERLAY_FUNCTION,
  TARGET_MENU_SHABU_SUKIYAKI,
} from 'utils';
import './style.scss';

const TABLE_COL_WIDTH = [10, 30, 60];

interface TargetMenuModalProps {
  targetSourceType?: number;
  targetName?: string;
  onLoadData?: () => Promise<CommonResponse<TargetMenu[]>>;
  onAdd?: (menuIds: number[]) => Promise<CommonResponse<any>>;
  onDelete?: (menuIds: number[]) => Promise<CommonResponse<any>>;
  onClose?: (isDataChanged: boolean) => void;
}

const TargetMenuModal = ({
  targetSourceType = TARGET_MENU_DEFAULT,
  targetName = '',
  onLoadData,
  onAdd,
  onDelete,
  onClose,
}: TargetMenuModalProps) => {
  const { t } = useTranslation();
  const { showGlobalModal } = useGlobalModal();

  // selectors
  const isEditMode = useAppSelector(selectIsEditMode);

  // state
  const [targetMenus, setTargetMenus] = useState<TargetMenu[]>([]);
  const [isOpenSelectTargetMenuModal, setIsOpenSelectTargetMenuModal] = useState(false);

  // refs
  const selectedTargetMenuIdsRef = useRef<number[]>([]);
  const btnDeleteRef = useRef<HTMLButtonElement>(null);

  // useEffect
  useEffect(() => {
    handleLoadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // functions
  function handleLoadData() {
    if (onLoadData) {
      (async () => {
        try {
          const response = await onLoadData();
          setTargetMenus(response.data);

          selectedTargetMenuIdsRef.current = [];
          if (btnDeleteRef.current) {
            btnDeleteRef.current.disabled = true;
          }

          const selectTargetMenuElements = document.getElementsByName(
            'selected_target_menu'
          ) as NodeListOf<HTMLInputElement>;
          if (selectTargetMenuElements.length > 0) {
            selectTargetMenuElements.forEach((selectTargetMenuElement) => {
              selectTargetMenuElement.checked = false;
            });
          }
        } catch (e) {
          if (!handleServiceError(showGlobalModal, e as AxiosError)) {
            displayError(showGlobalModal, e as AxiosError);
          }
        }
      })();
    }
  }

  function getTargetMenuName() {
    let targetMenuName: string | null = null;
    if (targetSourceType === TARGET_MENU_BAR_GUIDANCE) {
      targetMenuName = t('TargetMenuModal.target_menu_bar_guidance', { name: targetName });
    } else if (targetSourceType === TARGET_MENU_OVERLAY_FUNCTION) {
      targetMenuName = t('TargetMenuModal.target_menu_overlay_function', { name: targetName });
    } else if (targetSourceType === TARGET_MENU_DAILY_MENU) {
      targetMenuName = t('TargetMenuModal.target_menu_daily_menu', { name: targetName });
    } else if (targetSourceType === TARGET_MENU_ORDER_QUANTITY_LIMIT) {
      targetMenuName = t('TargetMenuModal.target_menu_quantity_order_limit', { name: targetName });
    } else if (targetSourceType === TARGET_MENU_SHABU_SUKIYAKI) {
      targetMenuName = t('TargetMenuModal.target_menu_shabu_sukiyaki', { name: targetName });
    }
    return targetMenuName;
  }

  function getConfirmDeleteMessage() {
    let message = t('TargetMenuModal.Warn.warn_confirm_delete_default');
    if (targetSourceType === TARGET_MENU_OVERLAY_FUNCTION) {
      message = t('TargetMenuModal.Warn.warn_confirm_delete_overlay_function');
    } else if (targetSourceType === TARGET_MENU_ORDER_QUANTITY_LIMIT) {
      message = t('TargetMenuModal.Warn.warn_confirm_delete_menu_quantity_order_limit');
    }

    return message;
  }

  function updateStatusDisableButtonDelete() {
    if (btnDeleteRef.current) {
      if (selectedTargetMenuIdsRef.current.length === 0) {
        btnDeleteRef.current.disabled = true;
      } else {
        btnDeleteRef.current.disabled = false;
      }
    }
  }

  // target menu action
  const handleSelectTargetMenu = (e: ChangeEvent<HTMLInputElement>) => {
    const menuId = parseInt(e.target.value, 10);
    const isExisted = selectedTargetMenuIdsRef.current.includes(menuId);

    if (e.target.checked) {
      if (!isExisted) {
        selectedTargetMenuIdsRef.current.push(menuId);
      }
    } else {
      if (isExisted) {
        selectedTargetMenuIdsRef.current = selectedTargetMenuIdsRef.current.filter(
          (removeMenuId) => !selectedTargetMenuIdsRef.current.includes(removeMenuId)
        );
      }
    }
    updateStatusDisableButtonDelete();
  };

  const deleteTargetMenu = () => {
    const deleteMenuIds = selectedTargetMenuIdsRef.current;
    if (onDelete && deleteMenuIds.length > 0) {
      (async () => {
        try {
          // process delete data
          await onDelete(deleteMenuIds);
          let message = t('TargetMenuModal.delete_success');
          // notify process success
          showGlobalModal(() => <AlertModal title={t('App.info')} message={message} />);

          // reload data
          handleLoadData();
        } catch (e) {
          if (!handleServiceError(showGlobalModal, e as AxiosError)) {
            displayError(showGlobalModal, e as AxiosError);
          }
        }
      })();
    }
  };

  const handleDeleteTargetMenu = () => {
    const confirmDeleteMessage = getConfirmDeleteMessage();
    showGlobalModal(() => (
      <ConfirmModal title={t('App.confirm')} message={confirmDeleteMessage} onConfirm={deleteTargetMenu} />
    ));
  };

  function handleCloseTargetMenuModal() {
    onClose?.(false);
  }

  // SelectTargetMenuModal events
  function handleOpenSelectTargetMenuModal() {
    setIsOpenSelectTargetMenuModal(true);
  }

  function handleCloseSelectTargetMenuModal() {
    setIsOpenSelectTargetMenuModal(false);
  }

  function handleReflectSelectTargetMenuModal(menuIds: number[]) {
    if (onAdd && menuIds.length > 0) {
      (async () => {
        try {
          // process add data
          await onAdd(menuIds);
          // notify process success
          showGlobalModal(() => <AlertModal title={t('App.info')} message={t('TargetMenuModal.save_success')} />);
          // reload data
          handleLoadData();
        } catch (e) {
          if (!handleServiceError(showGlobalModal, e as AxiosError)) {
            displayError(showGlobalModal, e as AxiosError);
          }
        }
      })();
    }
  }

  // compose value
  const targetMenuName = getTargetMenuName();

  return (
    <>
      <Modal show backdrop="static" dialogClassName="target-menu-modal" onHide={handleCloseTargetMenuModal} centered>
        <Modal.Header closeButton className={isEditMode ? 'reedit-mode-wrapper' : ''}>
          <FeatureTitleBar title={t('TargetMenuModal.Title.head_title')}></FeatureTitleBar>
        </Modal.Header>
        <Modal.Body className={isEditMode ? 'reedit-mode-wrapper' : ''}>
          {targetMenuName && <div className="fw-bold mb-0">{targetMenuName}</div>}

          <div className="text-end mb-2">
            <Button type="submit" className="btn--dark-green" onClick={handleOpenSelectTargetMenuModal}>
              {t('TargetMenuModal.Button.add_new')}
            </Button>
            <Button ref={btnDeleteRef} className="btn--red ms-3" onClick={handleDeleteTargetMenu}>
              {t('TargetMenuModal.Button.delete')}
            </Button>
          </div>
          <div className="dan-sticky-table" style={{ height: 360, maxHeight: 360 }}>
            <div className="dan-table">
              <div className="dan-thead">
                <div className="dan-tr">
                  <div className="dan-th" style={{ padding: '0.5rem', width: TABLE_COL_WIDTH[0] + '%' }}>
                    <div>{t('TargetMenuModal.Table.selection')}</div>
                  </div>
                  <div className="dan-th" style={{ padding: '0.5rem', width: TABLE_COL_WIDTH[1] + '%' }}>
                    <div>{t('TargetMenuModal.Table.menu_code')}</div>
                  </div>
                  <div className="dan-th" style={{ padding: '0.5rem', width: TABLE_COL_WIDTH[2] + '%' }}>
                    <div>{t('TargetMenuModal.Table.name')}</div>
                  </div>
                </div>
              </div>
              <div>
                {targetMenus.map((menu: TargetMenu) => {
                  return (
                    <div key={menu.id} className="dan-tr">
                      <div
                        className="dan-td d-flex justify-content-center flex-grow-0 flex-shrink-0"
                        style={{
                          width: TABLE_COL_WIDTH[0] + '%',
                        }}
                      >
                        <Form.Check
                          name="selected_target_menu"
                          className="selected-target-menu custom-checkbox"
                          defaultChecked={selectedTargetMenuIdsRef.current.includes(menu.id)}
                          onChange={handleSelectTargetMenu}
                          defaultValue={menu.id}
                        ></Form.Check>
                      </div>
                      <div
                        className="dan-td d-flex justify-content-center flex-grow-0 flex-shrink-0"
                        style={{
                          width: TABLE_COL_WIDTH[1] + '%',
                        }}
                      >
                        {menu.cd}
                      </div>
                      <div
                        className="dan-td d-flex justify-content-center flex-grow-0 flex-shrink-0"
                        style={{
                          width: TABLE_COL_WIDTH[2] + '%',
                        }}
                      >
                        {menu.name}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>
      {isOpenSelectTargetMenuModal &&
        createPortal(
          <SelectTargetMenuModal
            targetSourceType={targetSourceType}
            targetName={targetName}
            exceptMenuIds={targetMenus.map((menu) => menu.id)}
            onReflect={handleReflectSelectTargetMenuModal}
            onClose={handleCloseSelectTargetMenuModal}
          />,
          document.body
        )}
    </>
  );
};

export default TargetMenuModal;
